import {
  addFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  fetchServiceRequestFaqsNotesDocuments,
  updateFaqsNotesDocuments,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { transformServiceRequestFaqsNotesDocuments } from 'src/pages/ServiceRequest/CustomHooks/helpers';
import { useState } from 'react';
import { upload } from 'src/helpers/upLoadHelper';
import {
  prepareCreateNoteDataForAPI,
  prepareUpdateNoteDataForAPI,
} from 'src/SharedCustomHooks/UseNotesAPI/helpers';
import {
  IAddNoteServiceRequestPayload,
  INoteCreateInput,
  INoteFormConfig,
  INoteUpdateInput,
  IRawNoteFormData,
} from 'src/SharedCustomHooks/UseNotesAPI/types';

interface UseNotesAPIState {
  notes: any[];
  loadingNotes: boolean;
  error: string | null;
}

export interface UseNotesAPIReturn {
  notes: any[];
  loadingNotes: boolean;
  notesError: string | null;
  fetchNotes: (referenceId: string) => Promise<void>;
  saveNotes: (
    input: INoteCreateInput,
    referenceId: string,
    noteConfig: INoteFormConfig
  ) => Promise<void>;
  deleteNote: (noteData: any, referenceId: string) => Promise<void>;
  updateNote: (
    noteId: string,
    input: INoteUpdateInput,
    referenceId: string,
    noteConfig: INoteFormConfig,
    ticketNumber?: string
  ) => Promise<void>;
  clearNotesState: () => void;
}

export const useNotesAPI = (user: any, token?: string): UseNotesAPIReturn => {
  const [state, setState] = useState<UseNotesAPIState>({
    notes: [],
    loadingNotes: false,
    error: null,
  });

  const setLoadingNotes = (loading: boolean) => {
    setState((prev: UseNotesAPIState) => ({ ...prev, loadingNotes: loading }));
  };

  const setError = (error: string | null) => {
    setState((prev: UseNotesAPIState) => ({ ...prev, error }));
  };

  const fetchNotes = async (referenceId: string): Promise<void> => {
    try {
      setLoadingNotes(true);

      const response: any = await fetchServiceRequestFaqsNotesDocuments(
        referenceId,
        token
      );

      if (response.ok || response.status === 200) {
        const rawData = await response.json();

        const transformedData: any = transformServiceRequestFaqsNotesDocuments(
          rawData,
          user
        );

        const fetchedNotes = transformedData.notes || [];

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          notes: fetchedNotes,
        }));
      } else {
        console.warn('Failed to fetch notes');
      }
    } catch (error: any) {
      console.error('Error fetching notes:', error);
      setError(error.message || 'Failed to fetch notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const saveNotes = async (
    input: INoteCreateInput,
    referenceId: string,
    noteConfig: INoteFormConfig
  ): Promise<void> => {
    const uploadedImageUrl: string = input.image
      ? ((await upload(input.image)) ?? '')
      : '';

    const rawNoteData: IRawNoteFormData = {
      title: input.title.trim(),
      body: input.body.trim(),
      noteVisibleToHomeownerOccupantRealtor:
        input.noteVisibleToHomeownerOccupantRealtor,
      visibilityType: '',
      image: uploadedImageUrl,
    };

    const finalAPINoteData: any = prepareCreateNoteDataForAPI(
      rawNoteData,
      referenceId,
      user,
      noteConfig
    );

    try {
      setLoadingNotes(true);
      setError(null);

      const response: any = await addFaqsNotesDocuments(
        finalAPINoteData,
        token
      );

      if (response.ok || response.status === 200) {
        await response.json();

        toast.success(`ⓘ Note has been added.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        await fetchNotes(referenceId);
      } else {
        throw new Error(`Note adding failed`);
      }
    } catch (error: any) {
      console.error('Error saving note:', error);
      const errorMessage =
        error.message || `Note adding failed, please try again.`;
      setError(errorMessage);
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotes(false);
    }
  };

  const deleteNote = async (
    noteData: any,
    referenceId: string
  ): Promise<void> => {
    try {
      setLoadingNotes(true);
      setError(null);

      const response: any = await deleteFaqsNotesDocuments(noteData.id);

      if (response.ok || response.status === 200) {
        await response.json();

        toast.success(`ⓘ Note deleted successfully.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => {
          const notesWithoutDeletedItem = prev.notes.filter(
            (item: any) => item.id !== noteData.id
          );

          return {
            ...prev,
            notes: notesWithoutDeletedItem,
          };
        });

        await fetchNotes(referenceId);
      } else {
        throw new Error(`Note delete failed, please try again.`);
      }
    } catch (error: any) {
      console.error('Error deleting note:', error);
      const errorMessage =
        error.message || 'Note deletion failed, please try again.';
      setError(errorMessage);
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotes(false);
    }
  };

  const updateNote = async (
    noteId: string,
    input: INoteUpdateInput,
    referenceId: string,
    noteConfig: INoteFormConfig,
    ticketNumber?: string
  ): Promise<void> => {
    const existingNote: any = state.notes.find((n: any) => n.id === noteId);

    if (!existingNote) {
      console.warn(
        `[useNotesAPI] updateNote called for unknown noteId=${noteId}`
      );
      toast.error('Could not find this note to update. Please refresh.');
      return;
    }

    const uploadedImageUrl: string = input.image
      ? ((await upload(input.image)) ?? '')
      : (input.existingImageUrl ?? '');

    // WEB-1384: author + createdAt are set once at creation and immutable on
    // update. We rebuild the payload from the persisted note and overlay only
    // the form-editable fields — caller-supplied identity/timestamp fields
    // cannot reach the API.
    const mergedNote: IRawNoteFormData = {
      ...existingNote,
      title: input.title.trim(),
      body: input.body.trim(),
      noteVisibleToHomeownerOccupantRealtor:
        input.noteVisibleToHomeownerOccupantRealtor,
      image: uploadedImageUrl,
      author: existingNote.author,
      createdAt: existingNote.createdAt,
    };

    const finalAPINoteData: IAddNoteServiceRequestPayload =
      prepareUpdateNoteDataForAPI(
        mergedNote,
        referenceId,
        user,
        noteConfig,
        ticketNumber
      );

    try {
      setLoadingNotes(true);
      setError(null);

      const response: any = await updateFaqsNotesDocuments(
        finalAPINoteData,
        token
      );

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        setState((prev: UseNotesAPIState) => {
          const notesWithUpdatedItem = prev.notes.map((item: any) =>
            item.id === finalAPINoteData.id ? responseData.data : item
          );

          return {
            ...prev,
            notes: notesWithUpdatedItem,
          };
        });

        await fetchNotes(referenceId);
      } else {
        throw new Error(`Note updating failed`);
      }
    } catch (error: any) {
      console.error('Error updating note:', error);
      const errorMessage =
        error.message || `Note updating failed, please try again.`;
      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotes(false);
    }
  };

  const clearNotesState = () => {
    setState((prev: UseNotesAPIState) => ({
      ...prev,
      notes: [],
    }));
  };

  return {
    notes: state.notes,
    loadingNotes: state.loadingNotes,
    notesError: state.error,
    fetchNotes,
    saveNotes,
    deleteNote,
    updateNote,
    clearNotesState,
  };
};
